import { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import '../../styles/styles.css'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, StoreState } from '../../redux/store'
import { BranchType, fetchBranchAction } from '../../redux/actions/branchAction'

export type GstCustomerType = '' | 'B2B' | 'B2C'
export type GstDayFilter = 'ALL' | '0' | '1' | '2' | '3' | '4' | '5' | '6'

export interface GstSalesFilterState {
  customerType: GstCustomerType
  year: Date
  month: Date
  weekNumber: number // 0 = ALL
  day: GstDayFilter // ALL or JS day index (0=Sun..6=Sat) as string for selects
  fromDate: Date
  toDate: Date
  branchId: string | null
}

interface Props {
  onSearch: (filters: GstSalesFilterState) => void
  /** When false, hides B2B/B2C filter (e.g. GST purchase reports). */
  showCustomerType?: boolean
}

const getWeeksInMonth = (date: Date): number => {
  const startDate = new Date(date.getFullYear(), date.getMonth(), 1)
  const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  const diffInTime = endDate.getTime() - startDate.getTime()
  const totalDays = diffInTime / (1000 * 3600 * 24)
  return Math.ceil(totalDays / 7)
}

const getWeekNumber = (date: Date): number => {
  const firstJan = new Date(date.getFullYear(), 0, 1)
  const days = Math.floor((date.getTime() - firstJan.getTime()) / (24 * 60 * 60 * 1000))
  return Math.ceil((days + firstJan.getDay() + 1) / 7)
}

const clampDateToMonthEnd = (date: Date, monthEnd: Date) => (date > monthEnd ? monthEnd : date)

const getSpecificDateForWeekAndDay = (
  month: Date,
  weekNumber: number,
  day: GstDayFilter
): Date | null => {
  if (weekNumber <= 0) return null
  if (day === 'ALL') return null

  const dayIndex = Number(day) // 0=Sun..6=Sat
  if (Number.isNaN(dayIndex) || dayIndex < 0 || dayIndex > 6) return null

  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1)
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0)

  const weekStart = new Date(monthStart)
  weekStart.setDate(monthStart.getDate() + (weekNumber - 1) * 7)
  if (weekStart > monthEnd) return null

  const weekEnd = clampDateToMonthEnd(new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6), monthEnd)

  // find the first matching weekday in [weekStart..weekEnd]
  const diff = (dayIndex - weekStart.getDay() + 7) % 7
  const candidate = new Date(weekStart)
  candidate.setDate(weekStart.getDate() + diff)

  if (candidate > weekEnd) return null
  return candidate
}

const GstSalesReportFilter = ({ onSearch, showCustomerType = true }: Props) => {
  const today = new Date()
  const dispatch = useDispatch<AppDispatch>()
  const branch = useSelector<StoreState, BranchType[]>((state) => state.branch)

  const [weekNumbers, setWeekNumbers] = useState<number[]>([])

  const [filters, setFilters] = useState<GstSalesFilterState>({
    customerType: '',
    year: today,
    month: today,
    weekNumber: 0,
    day: 'ALL',
    fromDate: today,
    toDate: today,
    branchId: null,
  })

  useEffect(() => {
    dispatch(fetchBranchAction())
  }, [dispatch])

  useEffect(() => {
    const weekCount = getWeeksInMonth(filters.month)
    setWeekNumbers(Array.from({ length: weekCount }, (_, i) => i + 1))
  }, [filters.month])

  const handleYearChange = (date: Date | null) => {
    if (!date) return

    const newYear = date.getFullYear()
    const firstDayOfYear = new Date(newYear, 0, 1)
    const firstMonth = new Date(newYear, 0, 1)
    const monthStart = new Date(newYear, firstMonth.getMonth(), 1)
    const monthEnd = new Date(newYear, firstMonth.getMonth() + 1, 0)

    setFilters((prev) => ({
      ...prev,
      year: date,
      month: firstMonth,
      weekNumber: 0,
      day: 'ALL',
      fromDate: monthStart,
      toDate: monthEnd,
    }))
  }

  const handleMonthChange = (date: Date | null) => {
    if (!date) return

    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1)
    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0)

    setFilters((prev) => ({
      ...prev,
      month: date,
      weekNumber: 0, // ALL
      day: 'ALL', // ALL
      fromDate: monthStart,
      toDate: monthEnd,
    }))
  }

  const handleDateChange = (date: Date | null, name: 'fromDate' | 'toDate') => {
    if (!date) return

    setFilters((prev) => {
      return { ...prev, [name]: date }
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFilters((prev) => {
      if (name === 'weekNumber') {
        const nextWeekNumber = Number(value)
        const specific = getSpecificDateForWeekAndDay(prev.month, nextWeekNumber, prev.day)
        return {
          ...prev,
          weekNumber: nextWeekNumber,
          ...(specific ? { fromDate: specific, toDate: specific } : null),
        }
      }

      if (name === 'day') {
        const nextDay = value as GstDayFilter
        const specific = getSpecificDateForWeekAndDay(prev.month, prev.weekNumber, nextDay)
        return {
          ...prev,
          day: nextDay,
          ...(specific ? { fromDate: specific, toDate: specific } : null),
        }
      }
      if (name === 'customerType') return { ...prev, customerType: value as GstCustomerType }
      if (name === 'branchId') return { ...prev, branchId: value }
      return prev
    })
  }

  const handleReset = () => {
    setFilters({
      customerType: '',
      year: today,
      month: today,
      weekNumber: 0,
      day: 'ALL',
      fromDate: today,
      toDate: today,
      branchId: null,
    })
  }

  const handleSearchClick = () => {
    onSearch(filters)
  }

  return (
    <Card className="p-2 shadow-lg border-0 rounded-3">
      <Form>
        <Row className="mb-3">
          {showCustomerType && (
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fw-semibold">Customer Type</Form.Label>
                <Form.Select name="customerType" value={filters.customerType} onChange={handleChange}>
                  <option value="">All</option>
                  <option value="B2B">B2B</option>
                  <option value="B2C">B2C</option>
                </Form.Select>
              </Form.Group>
            </Col>
          )}

          <Col md={2}>
            <Form.Group>
              <Form.Label className="fw-semibold">Year</Form.Label>
              <DatePicker
                selected={filters.year}
                onChange={(date) => handleYearChange(date)}
                showYearPicker
                dateFormat="yyyy"
                className="form-control"
              />
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label className="fw-semibold">Month</Form.Label>
              <DatePicker
                selected={filters.month}
                onChange={(date) => handleMonthChange(date)}
                showMonthYearPicker
                dateFormat="MMMM"
                className="form-control"
              />
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label className="fw-semibold">Week Number</Form.Label>
              <Form.Select name="weekNumber" value={filters.weekNumber} onChange={handleChange}>
                <option value={0}>All</option>
                {weekNumbers.map((week) => (
                  <option key={week} value={week}>
                    Week {week}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label className="fw-semibold">Day</Form.Label>
              <Form.Select name="day" value={filters.day} onChange={handleChange}>
                <option value="ALL">All</option>
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
                <option value="0">Sunday</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label className="fw-semibold">Branch</Form.Label>
              <Form.Select name="branchId" value={filters.branchId ?? ''} onChange={handleChange}>
                <option value="All">All</option>
                {branch.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={2}>
            <Form.Group>
              <Form.Label className="fw-semibold">From Date</Form.Label>
              <DatePicker
                selected={filters.fromDate}
                onChange={(date) => handleDateChange(date, 'fromDate')}
                dateFormat="dd/MM/yyyy"
                className="form-control"
              />
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label className="fw-semibold">To Date</Form.Label>
              <DatePicker
                selected={filters.toDate}
                onChange={(date) => handleDateChange(date, 'toDate')}
                dateFormat="dd/MM/yyyy"
                className="form-control"
              />
            </Form.Group>
          </Col>

          <div className="d-flex justify-content-end mb-1">
            <Button className="btn-sm fw-bold" variant="secondary" onClick={handleReset}>
              Reset
            </Button>
            <Button className="btn-sm fw-bold ms-2" variant="success" onClick={handleSearchClick}>
              Search
            </Button>
          </div>
        </Row>
      </Form>
    </Card>
  )
}

export default GstSalesReportFilter

